import EmptyPageContent from '@app/ccf/components/empty-page-content';
import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import Text from '@shared/components/content/text';

import { getReportsPathMap } from '@app/components/reports/utils';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import {
  ActivityImportList,
  OperationReportTypeEnum,
  PaginatedActivityImportListList,
} from '@shared/api/types';
import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useActivityImportTableColumns } from './activity-import-columns';
import ActivityImportEditForm from './activity-import-edit-form';

interface ActivityImportTableProps {
  data: PaginatedActivityImportListList;
  pageSize: number;
  status?: string;
  reportType: OperationReportTypeEnum;
}

export default function ActivityImportTable({
  data,
  pageSize,
  status,
  reportType,
}: ActivityImportTableProps) {
  const navigate = useNavigate();
  const [selectedImportData, setSelectedImportData] =
    useState<ActivityImportList>();

  const {
    showSimpleDialog: showEditDialog,
    SimpleDialogComponent: EditDialog,
    closeSimpleDialog: closeEditDialog,
  } = useSimpleDialog();

  const { results: foodImports, count } = data || {};
  const pageCount = count ? Math.ceil(count / (pageSize || 10)) : 0;

  const { table } = useServerDataTable({
    data: foodImports || [],
    columns: useActivityImportTableColumns({
      reportType,
    }),
    shallow: false,
    pageCount,
    initialState: {
      columnVisibility: { uuid: false },
    },
    getRowId: (row) => row.uuid,
  });

  if (foodImports && count < 1) {
    // Both purchased and sold goods use the single unified import flow, so the
    // empty state offers one "Import data" CTA to the wizard for either side.
    return (
      <EmptyPageContent
        description={
          <div className="mt-20 space-y-4">
            <Text className="text-center">
              You have no imports yet. Get started by uploading your data.
            </Text>
          </div>
        }
        primaryAction={() =>
          navigate(generatePath(getReportsPathMap()[reportType].foodImport!))
        }
        primaryActionText="Import data"
      />
    );
  }

  return (
    <>
      <DataTable
        table={table}
        isLoading={status === 'loading'}
        className="bg-gray-00"
        rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
        onRowClick={(row) => {
          setSelectedImportData(row?.original);
          showEditDialog();
        }}
      >
        <DataTableToolbar table={table} filterOrder={['fileName']} />
      </DataTable>
      {EditDialog({
        header: 'Edit import',
        contentClassName: 'max-w-lg',
        content: (
          <ActivityImportEditForm
            importData={selectedImportData}
            closeDialog={closeEditDialog}
          />
        ),
      })}
    </>
  );
}
